import { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { sprinkles, Tag, Intent } from 'components/ds';
import { IconName } from 'components/ds/Icon';
import { Intent as BlueprintIntent } from '@blueprintjs/core';

import { SAVE_RESOURCE_CONFIG_EVENT } from 'utils/customEventUtils';
import { clearSavingReducer, setDebounceSaving } from 'reducers/resourceSavingReducer';
import { ReduxState } from 'reducers/rootReducer';
import { createDebouncedFn } from 'utils/general';
import { ResourcePageType } from 'types/exploResource';
import { saveResourceConfig } from 'reducers/thunks/resourceSaveThunks';
import { showCustomToast } from 'shared/sharedToasts';

const debounceFn = createDebouncedFn(2000);

type Props = { resourceType: ResourcePageType; versionNumber: number; isDraft: boolean };

export const SavingInfo: FC<Props> = ({ resourceType, versionNumber, isDraft }) => {
  const dispatch = useDispatch();

  const savingInfo = useSelector((state: ReduxState) => state.resourceSaving);

  useEffect(() => {
    return () => {
      dispatch(clearSavingReducer());
    };
  }, [dispatch]);

  useEffect(() => {
    if (savingInfo.isSaving || !savingInfo.changesWhileSaving) return;
    dispatch(saveResourceConfig(resourceType));
  }, [dispatch, resourceType, savingInfo.isSaving, savingInfo.changesWhileSaving]);

  const saveConfigChanges = useCallback(() => {
    dispatch(setDebounceSaving(true));
    debounceFn(() => dispatch(saveResourceConfig(resourceType)));
  }, [dispatch, resourceType]);

  useEffect(() => {
    //@ts-ignore
    window.addEventListener(SAVE_RESOURCE_CONFIG_EVENT, saveConfigChanges);
    return () => {
      //@ts-ignore
      window.removeEventListener(SAVE_RESOURCE_CONFIG_EVENT, saveConfigChanges);
    };
  }, [saveConfigChanges]);

  useEffect(() => {
    if (!savingInfo.errorMessage) return;
    showCustomToast(savingInfo.errorMessage || '', {
      timeoutInSeconds: 5,
      icon: 'error',
      intent: BlueprintIntent.DANGER,
      action: {
        target: '_blank',
        icon: 'refresh',
        text: 'Refresh',
        onClick: () => window.location.reload(),
      },
    });
  }, [savingInfo.errorMessage]);

  const alertUser = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!savingInfo.isSaving && !savingInfo.changesWhileSaving && !savingInfo.errorSaving) return;
      event.preventDefault();
      event.returnValue = '';
    },
    [savingInfo],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    return () => {
      window.removeEventListener('beforeunload', alertUser);
    };
  }, [alertUser]);

  let icon: IconName = 'check';
  if (savingInfo.isSaving || savingInfo.changesWhileSaving || savingInfo.debounceSaving) {
    icon = 'arrows-rotate';
  } else if (savingInfo.errorSaving) icon = 'circle-exclamation';

  return (
    <Tag className={sprinkles({ marginRight: 'sp1' })} intent={Intent.NONE} leftIcon={icon}>
      {`Version: ${versionNumber}${isDraft ? ' (draft)' : ''}`}
    </Tag>
  );
};
